// src/utils/photoVehicles.js
//
// Which car a load photo belongs to.
//
// On a multi-car load the driver app files every photo against a car: it
// stores vehicleIndex (0-based position in load.vehicles), vehicleInfo
// ("2024 Ram 1500") and vehicleVin. The online BOL used to ignore all three,
// so a customer receiving three cars saw one pile of odometers and VIN plates
// with no way to tell whose was whose.

const clean = (v) => (v == null ? '' : String(v).trim());

/** "2024 Ram 1500" from a load.vehicles entry. */
function describeVehicle(v) {
  if (!v) return '';
  return [v.year, v.make, v.model].map(clean).filter(Boolean).join(' ');
}

/** A stable key for the car a photo was filed against, or null. */
function vehicleKeyOf(photo) {
  if (!photo) return null;
  if (typeof photo.vehicleIndex === 'number') return `i:${photo.vehicleIndex}`;
  if (clean(photo.vehicleVin)) return `vin:${clean(photo.vehicleVin).toUpperCase()}`;
  if (clean(photo.vehicleInfo)) return `info:${clean(photo.vehicleInfo).toLowerCase()}`;
  return null;
}

/** The load.vehicles entry and position for a photo, matched by index then VIN. */
function matchVehicle(photo, vehicles) {
  const list = Array.isArray(vehicles) ? vehicles : [];
  if (typeof photo.vehicleIndex === 'number' && list[photo.vehicleIndex]) {
    return { vehicle: list[photo.vehicleIndex], position: photo.vehicleIndex };
  }
  const vin = clean(photo.vehicleVin).toUpperCase();
  if (vin) {
    const at = list.findIndex(v => clean(v && v.vin).toUpperCase() === vin);
    if (at >= 0) return { vehicle: list[at], position: at };
  }
  return { vehicle: null, position: typeof photo.vehicleIndex === 'number' ? photo.vehicleIndex : null };
}

/**
 * How to name the car a photo belongs to.
 * @returns {{ title: string, vin: string, position: number|null }}
 */
export function vehicleLabelOf(photo, vehicles) {
  const { vehicle, position } = matchVehicle(photo, vehicles);
  const name = describeVehicle(vehicle) || clean(photo.vehicleInfo);
  const vin = clean((vehicle && vehicle.vin) || photo.vehicleVin);
  const total = Array.isArray(vehicles) ? vehicles.length : 0;
  const number = position != null
    ? `Vehicle ${position + 1}${total > 1 ? ` of ${total}` : ''}`
    : 'Vehicle';
  return { title: name ? `${number} — ${name}` : number, vin, position };
}

/** Short VIN for display: the last six characters, which is what people read off a car. */
export const shortVin = (vin) => (clean(vin).length > 6 ? `…${clean(vin).slice(-6)}` : clean(vin));

/** True when this load's photos should be shown per car. */
export function shouldGroupByVehicle(photos, vehicles) {
  const list = Array.isArray(photos) ? photos : [];
  if ((Array.isArray(vehicles) ? vehicles.length : 0) > 1) return list.some(p => vehicleKeyOf(p));
  return new Set(list.map(vehicleKeyOf).filter(Boolean)).size > 1;
}

/**
 * Photos split by car, in the order the cars appear on the load. Photos not
 * filed against any car (taken before per-car photos existed) come last.
 * @returns {Array<{ key: string, title: string, vin: string, photos: object[] }>}
 */
export function groupPhotosByVehicle(photos, vehicles) {
  const groups = new Map();
  for (const photo of Array.isArray(photos) ? photos : []) {
    const key = vehicleKeyOf(photo) || 'none';
    if (!groups.has(key)) {
      if (key === 'none') {
        groups.set(key, { key, title: 'Not linked to a vehicle', vin: '', position: Infinity, photos: [] });
      } else {
        const label = vehicleLabelOf(photo, vehicles);
        groups.set(key, {
          key,
          title: label.title,
          vin: label.vin,
          position: label.position != null ? label.position : Number.MAX_SAFE_INTEGER,
          photos: [],
        });
      }
    }
    groups.get(key).photos.push(photo);
  }
  return [...groups.values()].sort((a, b) => a.position - b.position);
}

/** A folder-safe name for a car in a ZIP download. */
export function vehicleFolderName(photo, vehicles) {
  if (!vehicleKeyOf(photo)) return 'no_vehicle';
  const { title, vin } = vehicleLabelOf(photo, vehicles);
  return `${title}${vin ? ` ${vin.slice(-6)}` : ''}`
    .replace(/[^A-Za-z0-9 _-]+/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 80) || 'vehicle';
}
